import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import Layout from '../components/layout/Layout';
import CustomButton from '../components/CustomButton';
import TextField from '../components/TextField';
import { api } from '../redux/actions/api';

const TranslationEditor = () => {
  const selectedLanguage = useSelector((state) => state.app.selectedLanguage);
  const [translations, setTranslations] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState(new Set());
  const [editingKey, setEditingKey] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const loadTranslations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/locales/${selectedLanguage}`);
      setTranslations(response.data.translation || {});
    } catch (error) {
      console.error('Error loading translations:', error);
      alert('Failed to load translations');
    } finally {
      setLoading(false);
    }
  }, [selectedLanguage]);

  // Load translations when language changes
  useEffect(() => {
    if (selectedLanguage) {
      loadTranslations();
    }
  }, [selectedLanguage, loadTranslations]);

  const saveTranslation = async (path, value) => {
    try {
      setSaving(true);
      await api.patch(`/locales/${selectedLanguage}/path`, {
        path,
        value,
      });
      alert('Translation updated successfully');
      // Update local state
      const keys = path.split('.');
      const newTranslations = { ...translations };
      let current = newTranslations;
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      setTranslations(newTranslations);
    } catch (error) {
      console.error('Error saving translation:', error);
      alert('Failed to save translation');
    } finally {
      setSaving(false);
    }
  };

  const toggleExpanded = (key) => {
    const newExpanded = new Set(expandedKeys);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedKeys(newExpanded);
  };

  const startEditing = (path, value) => {
    setEditingKey(path);
    setEditingValue(value);
  };

  const cancelEditing = () => {
    setEditingKey(null);
    setEditingValue('');
  };

  const confirmEdit = (path) => {
    saveTranslation(path, editingValue);
    setEditingKey(null);
    setEditingValue('');
  };

  const filterTranslations = (obj, parentPath = '', query) => {
    if (!query) return true;

    const lowerQuery = query.toLowerCase();

    // Check if current path matches
    if (parentPath.toLowerCase().includes(lowerQuery)) {
      return true;
    }

    // If this is a string value, check if it matches
    if (typeof obj === 'string') {
      return obj.toLowerCase().includes(lowerQuery);
    }

    // If this is an object, check if any child matches
    if (typeof obj === 'object' && obj !== null) {
      return Object.entries(obj).some(([key, value]) => {
        const currentPath = parentPath ? `${parentPath}.${key}` : key;
        return filterTranslations(value, currentPath, query);
      });
    }

    return false;
  };

  const renderTranslationTree = (obj, parentPath = '', level = 0) => {
    if (!obj || typeof obj !== 'object') return null;

    return Object.entries(obj).map(([key, value]) => {
      const currentPath = parentPath ? `${parentPath}.${key}` : key;
      const isExpanded = expandedKeys.has(currentPath);
      const isEditing = editingKey === currentPath;
      const isString = typeof value === 'string';
      const isObject = typeof value === 'object' && value !== null;

      // Filter logic
      if (searchQuery && !filterTranslations(value, currentPath, searchQuery)) {
        return null;
      }

      return (
        <div key={currentPath} style={{ marginLeft: level * 20 }}>
          <div
            className="flex flex-row items-center py-1 px-2 hover:bg-gray-100 rounded"
            style={{ minHeight: '36px' }}
          >
            {isObject && (
              <button
                onClick={() => toggleExpanded(currentPath)}
                className="mr-2 text-sm font-bold focus:outline-none"
                style={{ width: '20px' }}
              >
                {isExpanded ? '▼' : '▶'}
              </button>
            )}
            {!isObject && <div style={{ width: '20px', marginRight: '8px' }} />}

            <span className="font-semibold text-sm" style={{ minWidth: '200px' }}>
              {key}
            </span>

            {isString && !isEditing && (
              <>
                <span className="text-sm ml-4 flex-1 text-gray-700">{value}</span>
                <CustomButton
                  onClick={() => startEditing(currentPath, value)}
                  className="ml-2 px-3 py-1 text-xs"
                >
                  Edit
                </CustomButton>
              </>
            )}

            {isString && isEditing && (
              <>
                <TextField
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  style={{ marginLeft: '10px', flex: 1 }}
                  multiline={editingValue.length > 100}
                  rows={editingValue.length > 100 ? 4 : 1}
                />
                <CustomButton
                  onClick={() => confirmEdit(currentPath)}
                  className="ml-2 px-3 py-1 text-xs bg-green-500"
                  loading={saving}
                >
                  Save
                </CustomButton>
                <CustomButton onClick={cancelEditing} className="ml-1 px-3 py-1 text-xs" red>
                  Cancel
                </CustomButton>
              </>
            )}

            {!isString && !isObject && (
              <span className="text-sm ml-4 text-gray-500">
                {typeof value} - {JSON.stringify(value)}
              </span>
            )}
          </div>

          {isObject && isExpanded && renderTranslationTree(value, currentPath, level + 1)}
        </div>
      );
    });
  };

  return (
    <Layout active="translations">
      <div className="p-6">
        <h1 className="text-3xl font-bold mb-6">
          Translation Editor - {selectedLanguage.toUpperCase()}
        </h1>

        {/* Reload Button */}
        <div className="mb-6">
          <CustomButton onClick={loadTranslations} loading={loading}>
            Reload Translations
          </CustomButton>
        </div>

        {/* Search */}
        {selectedLanguage && (
          <div className="mb-4" style={{ width: '500px' }}>
            <TextField
              label="Search translations (by key or value)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="e.g., common.message or 'hello'"
            />
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-8">
            <p className="text-lg">Loading...</p>
          </div>
        )}

        {/* Translation Tree */}
        {!loading && selectedLanguage && Object.keys(translations).length > 0 && (
          <div className="bg-white rounded-lg shadow p-4">
            <h2 className="text-xl font-semibold mb-4">
              Translations for {selectedLanguage.toUpperCase()}
            </h2>
            <div className="max-h-screen overflow-y-auto">
              {renderTranslationTree(translations)}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && selectedLanguage && Object.keys(translations).length === 0 && (
          <div className="text-center py-8">
            <p className="text-lg text-gray-500">No translations found for this language</p>
          </div>
        )}

        {/* No Language Selected */}
        {!loading && !selectedLanguage && (
          <div className="text-center py-8">
            <p className="text-lg text-gray-500">Please select a language to begin editing</p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TranslationEditor;
